import type { ShapeData } from '@/lib/shapes/types/shape-data'
import {
  DEBUG_PIVOT_COLOR,
  debugBoundingBoxesForShape,
  debugPivotForShape,
} from '@/lib/shapes/debug-bounding-boxes'

/**
 * Geometry debug overlay: every bounding box `shape-coordinates.ts` can produce, for every shape.
 *
 * Purely presentational — the visual layer owns polling, flags and the `<svg>` host. Draws in
 * computed screen pixels (document coordinates × `scale`), so a 1px stroke is a true pixel and dash
 * patterns keep their spacing at any zoom, with no `vector-effect="non-scaling-stroke"` and no
 * device-pixel-grid snapping.
 *
 * **Off by default.** Three equivalent ways to enable:
 *
 * ```js
 * __larryVisualDebug(true)                            // console
 * localStorage.setItem('larry:visual-debug', '1')      // picked up within ~1s, no reload
 * // ?visualDebug=1                                    // URL param, survives reloads
 * ```
 *
 * To check the layer is mounted at all: `document.querySelector('[data-visual-layer]')`.
 */

/** Fixed screen size for the pivot marker — no zoom compensation, this layer is already in px. */
const PIVOT_RADIUS_PX = 3
/** Fixed screen thickness for every box outline. */
const BOX_STROKE_PX = 1

function pointsAttr(points: Array<{ x: number; y: number }>, scale: number): string {
  return points.map((p) => `${p.x * scale},${p.y * scale}`).join(' ')
}

export type DebugBoundingBoxesProps = {
  shapes: ShapeData[]
  scale: number
}

export function DebugBoundingBoxes({ shapes, scale }: DebugBoundingBoxesProps) {
  if (shapes.length === 0) return null

  return (
    <g data-visual-debug-boxes="">
      {shapes.map((shape) => {
        const boxes = debugBoundingBoxesForShape(shape)
        const pivot = debugPivotForShape(shape)

        return (
          <g key={shape.id}>
            {boxes.map((box) => (
              <polygon
                key={box.kind}
                points={pointsAttr(box.points, scale)}
                fill="none"
                stroke={box.color}
                strokeWidth={BOX_STROKE_PX}
                strokeDasharray={box.dashArray}
              />
            ))}
            <circle
              cx={pivot.x * scale}
              cy={pivot.y * scale}
              r={PIVOT_RADIUS_PX}
              fill={DEBUG_PIVOT_COLOR}
            />
          </g>
        )
      })}
    </g>
  )
}

export default DebugBoundingBoxes
